import { useCallback, useEffect, useRef, useState } from 'react';
import type { BulkInvitationJobDto } from '@genoma/shared';

import { useI18n } from '../../i18n/index.tsx';
import {
  cancelInvitationRun,
  fetchInvitationRun,
  startInvitationRun,
} from '../../services/dashboard.service.ts';
import { useCan } from '../../stores/auth.store.ts';

/**
 * Sending everybody who has never been invited their link, one at a time.
 *
 * The bar is not decoration. The run takes about half an hour by design — one
 * message every twenty seconds, so ninety near-identical emails do not arrive
 * at one corporate gateway in the same second and get filed as bulk — and an
 * operation that long with no visible progress is one an admin starts twice.
 *
 * The server owns the loop, so this component only watches: closing the tab
 * does not stop the sending, and reopening it finds the run still going.
 */
const POLL_MS = 3000;

type Translate = (key: string, values?: Record<string, string | number>) => string;

const formatDuration = (seconds: number, t: Translate) => {
  if (seconds <= 0) return t('invitationRun.almostDone');
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return t('invitationRun.underAMinute');
  return t('invitationRun.minutes', { count: minutes });
};

export const InvitationRun = ({ onFinished }: { onFinished: () => void }) => {
  const { t } = useI18n();
  const canRun = useCan('rater:invite:bulk');

  const [job, setJob] = useState<BulkInvitationJobDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // So the list can refresh itself the moment the last one goes out, without
  // this component knowing what a rater list is.
  const wasRunning = useRef(false);

  const load = useCallback(async () => {
    try {
      const next = await fetchInvitationRun();
      setJob(next);
      if (wasRunning.current && next.state !== 'RUNNING') onFinished();
      wasRunning.current = next.state === 'RUNNING';
    } catch {
      // A failed poll is not worth a red box on a screen that is mostly a
      // list of people: the next one is three seconds away.
    }
  }, [onFinished]);

  useEffect(() => {
    void load();
  }, [load]);

  // Polling only while something is happening. A screen left open on a
  // finished cycle should not talk to the server every three seconds all day.
  useEffect(() => {
    if (job?.state !== 'RUNNING') return;
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [job?.state, load]);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      setJob(await startInvitationRun());
      wasRunning.current = true;
      setConfirming(false);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      setJob(await cancelInvitationRun());
      onFinished();
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  if (!job) return null;

  const running = job.state === 'RUNNING';

  // Nothing running, nobody left to write to, and no run to report on: the
  // panel has nothing to say and takes up no room.
  if (!running && job.state !== 'DONE' && job.eligible === 0) return null;

  return (
    <section className="mt-5 rounded-2xl border border-line bg-paper p-4 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[14.5px] font-bold">{t('invitationRun.title')}</h2>
          <p className="mt-0.5 text-[13px] text-soft">
            {running
              ? t('invitationRun.leadRunning', {
                  sent: job.sent,
                  total: job.total,
                  eta: formatDuration(job.remainingSeconds, t),
                })
              : job.state === 'DONE'
                ? t('invitationRun.leadDone', { sent: job.sent })
                : t('invitationRun.leadIdle', {
                    count: job.eligible,
                    seconds: job.secondsBetween,
                    eta: formatDuration(job.remainingSeconds, t),
                  })}
          </p>
        </div>

        {canRun && (
          <div className="flex shrink-0 items-center gap-2">
            {running ? (
              <button
                type="button"
                onClick={() => void stop()}
                disabled={busy}
                className="rounded-xl border border-line px-3.5 py-2 text-[13px] font-semibold text-soft transition hover:border-[var(--color-critical)] hover:text-[var(--color-critical)] disabled:opacity-50"
              >
                {t('invitationRun.stop')}
              </button>
            ) : job.eligible > 0 ? (
              <button
                type="button"
                onClick={() => (confirming ? void start() : setConfirming(true))}
                disabled={busy}
                className={`rounded-xl px-3.5 py-2 text-[13px] font-semibold transition disabled:opacity-50 ${
                  confirming
                    ? 'bg-[var(--color-critical)] text-white'
                    : 'bg-teal text-white hover:bg-teal-deep'
                }`}
              >
                {confirming
                  ? t('invitationRun.confirm', { count: job.eligible })
                  : t('invitationRun.start', { count: job.eligible })}
              </button>
            ) : null}
          </div>
        )}
      </div>

      {(running || job.state === 'DONE') && (
        <>
          <div className="mt-3.5 flex items-center gap-3">
            <div
              role="progressbar"
              aria-valuenow={job.percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('invitationRun.title')}
              className="h-2 flex-1 overflow-hidden rounded-full bg-line-soft"
            >
              <div
                className="h-full rounded-full bg-teal transition-[width] duration-700 ease-out"
                style={{ width: `${job.percent}%` }}
              />
            </div>
            <span className="font-mono text-[12.5px] tabular-nums text-soft">{job.percent}%</span>
          </div>

          <p className="mt-2 font-mono text-[11px] tracking-[0.04em] text-faint">
            {t('invitationRun.counts', {
              sent: job.sent,
              total: job.total,
              failed: job.failed,
              skipped: job.skipped,
            })}
            {/* Naming who is being written to right now is the difference
                between a bar that might be stuck and one that is visibly
                working, at a cadence where nothing moves for twenty seconds. */}
            {running && job.currentName && ` · ${t('invitationRun.sendingTo', { name: job.currentName })}`}
          </p>
        </>
      )}

      {job.skipped > 0 && !running && (
        <p className="mt-2 text-[12.5px] text-soft">
          {t('invitationRun.skippedNote', { count: job.skipped })}
        </p>
      )}

      {job.failures.length > 0 && (
        <ul className="mt-2.5 space-y-1 border-t border-line-soft pt-2.5">
          {job.failures.map((failure) => (
            <li key={failure.email} className="text-[12.5px] text-[var(--color-critical)]">
              {failure.fullName} <span className="text-faint">({failure.email})</span> —{' '}
              {failure.reason}
            </li>
          ))}
        </ul>
      )}

      {confirming && !running && (
        <p className="mt-2.5 text-[12.5px] text-soft">
          {t('invitationRun.confirmNote', {
            count: job.eligible,
            eta: formatDuration(job.remainingSeconds, t),
          })}
        </p>
      )}

      {error && <p className="mt-2.5 text-[12.5px] text-[var(--color-critical)]">{error}</p>}
    </section>
  );
};
